import { useState, Fragment } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, parseErrorMessage, parseErrorCode } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import { usePropertyGroups } from "@/components/property-group-select";
import AppLayout from "@/components/app-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AccountType } from "@shared/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AuditLogTab from "@/components/team/audit-log-tab";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, UserPlus, Trash2, Mail, CheckCircle, XCircle, Clock, Loader2, Shield, Save, Pencil, Plus, BookUser, FolderOpen, AlertTriangle, X, Lock, Eye, Send, Bell } from "lucide-react";
import { formatDistanceToNow, format, differenceInDays } from "date-fns";
import type { TeamMember, AreaPermissions, AppArea, PermissionLevel, Property, RoleProfile } from "@shared/schema";
import { APP_AREAS, PERMISSION_LEVELS, DEFAULT_PERMISSIONS } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { BackToPropertyLink } from "@/components/back-to-property-link";
import { usePageTitle } from "@/hooks/use-page-title";

type TeamInvitation = TeamMember & { ownerName: string };
type EnrichedTeamMember = TeamMember & { inviteeAccountExists: boolean | null; lastInviteSentAt: string | null };

const AREA_LABELS: Record<AppArea, string> = {
  messages: "Messages",
  members: "Members",
  properties: "Properties",
  underwriting: "Underwriting",
  image_generator: "Image Generator",
  pricing: "Rooms/Pricing",
  maintenance: "Operations",
  financials: "Financials",
  devices: "Devices",
  furnishing: "Furnishing",
  team: "Team",
  invite: "Invite Teammates",
  padsplit_sync: "PadSplit Sync",
  owners: "Owners",
};

const LEVEL_LABELS: Record<PermissionLevel, string> = {
  disabled: "Disabled",
  view: "View",
  edit: "Edit",
};

function statusIcon(status: string) {
  if (status === "accepted") return <CheckCircle className="h-4 w-4 text-green-500" />;
  if (status === "declined") return <XCircle className="h-4 w-4 text-red-500" />;
  return <Clock className="h-4 w-4 text-yellow-500" />;
}

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLastSent(dateStr: string | Date | null) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const days = differenceInDays(new Date(), date);
  if (days < 7) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, "MMM d");
}

function PermissionGrid({
  permissions,
  onChange,
  onBulkChange,
  disabled,
}: {
  permissions: AreaPermissions;
  onChange: (area: AppArea, level: PermissionLevel) => void;
  onBulkChange?: (level: PermissionLevel) => void;
  disabled?: boolean;
}) {
  const allSameLevel = PERMISSION_LEVELS.find((level) =>
    APP_AREAS.every((area) => {
      if (area === "team" && level === "edit") return permissions[area] === "view";
      if (area === "invite" && level === "view") return permissions[area] === "disabled";
      return permissions[area] === level;
    })
  );

  return (
    <div className="grid grid-cols-1 gap-2">
      {onBulkChange && (
        <div className="flex items-center justify-between gap-3 py-1.5 mb-1 border-b border-gray-200 pb-3">
          <span className="text-sm font-semibold text-gray-800 min-w-[120px]">Set All</span>
          <div className="flex gap-1">
            {PERMISSION_LEVELS.map((level) => (
              <button
                key={`bulk-${level}`}
                type="button"
                disabled={disabled}
                onClick={() => onBulkChange(level)}
                data-testid={`perm-bulk-${level}`}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  allSameLevel === level
                    ? level === "edit"
                      ? "bg-blue-600 text-white border-blue-600"
                      : level === "view"
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-gray-500 text-white border-gray-500"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {LEVEL_LABELS[level]}
              </button>
            ))}
          </div>
        </div>
      )}
      {APP_AREAS.map((area) => {
        const allowedLevels = area === "team"
          ? PERMISSION_LEVELS.filter((l) => l !== "edit")
          : area === "invite"
            ? PERMISSION_LEVELS.filter((l) => l !== "view")
            : PERMISSION_LEVELS;
        return (
          <div key={area} className="flex items-center justify-between gap-3 py-1">
            <span className="text-sm font-medium text-gray-700 min-w-[120px]">{AREA_LABELS[area]}</span>
            <div className="flex gap-1">
              {allowedLevels.map((level) => (
                <button
                  key={level}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(area, level)}
                  data-testid={`perm-${area}-${level}`}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    permissions[area] === level
                      ? level === "edit"
                        ? "bg-blue-600 text-white border-blue-600"
                        : level === "view"
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-gray-500 text-white border-gray-500"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  {LEVEL_LABELS[level]}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PropertyGroupPicker({
  availableGroups,
  selectedGroups,
  onChange,
  disabled,
}: {
  availableGroups: string[];
  selectedGroups: string[] | null;
  onChange: (groups: string[] | null) => void;
  disabled?: boolean;
}) {
  const isAll = selectedGroups === null;
  const staleGroups = !isAll
    ? (selectedGroups || []).filter((g) => !availableGroups.includes(g))
    : [];

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center gap-2 mb-3">
        <FolderOpen className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-semibold text-gray-700">Property Groups</span>
      </div>
      {staleGroups.length > 0 && (
        <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50" data-testid="warning-stale-groups">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-800">
                {staleGroups.length === 1 ? "This group no longer exists" : "These groups no longer exist"}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Stored access references group{staleGroups.length === 1 ? "" : "s"} that have been deleted or renamed. They have no effect — remove them to clean up.
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {staleGroups.map((g) => (
                  <Badge
                    key={g}
                    variant="outline"
                    className="text-xs bg-white text-amber-800 border-amber-300 gap-1"
                    data-testid={`badge-stale-group-${g}`}
                  >
                    {g}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = (selectedGroups || []).filter((x) => x !== g);
                          onChange(next);
                        }}
                        className="ml-1 hover:text-amber-900"
                        aria-label={`Remove stale group ${g}`}
                        data-testid={`button-remove-stale-group-${g}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {availableGroups.length === 0 ? (
        <p className="text-xs text-gray-400">No property groups defined yet. Assign groups on the Properties page first.</p>
      ) : (
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="checkbox-groups-all">
            <Checkbox
              checked={isAll}
              onCheckedChange={(val) => onChange(val ? null : [])}
              disabled={disabled}
            />
            <span className="text-sm font-medium text-gray-700">All Groups</span>
          </label>
          <div className="ml-6 space-y-1.5">
            {availableGroups.map((group) => {
              const checked = isAll || (selectedGroups?.includes(group) ?? false);
              return (
                <label key={group} className="flex items-center gap-2 cursor-pointer" data-testid={`checkbox-group-${group}`}>
                  <Checkbox
                    checked={checked}
                    disabled={disabled || isAll}
                    onCheckedChange={(val) => {
                      if (isAll) return;
                      if (val) {
                        const next = [...(selectedGroups || []), group];
                        if (next.length === availableGroups.length) {
                          onChange(null);
                        } else {
                          onChange(next);
                        }
                      } else {
                        const next = (selectedGroups || []).filter((g) => g !== group);
                        onChange(next.length > 0 ? next : []);
                      }
                    }}
                  />
                  <span className="text-sm text-gray-600">{group}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

type AuthMeData = {
  user: { id: number; email: string; selectedPlan?: string };
  effectivePlan?: AccountType;
  planInherited?: boolean;
  planInviterName?: string | null;
  activeMembership?: { ownerUserId: number; ownerName: string; ownerEmail: string; role: string } | null;
};

function MembershipPanel({ membership }: { membership: { ownerUserId: number; ownerName: string; ownerEmail: string; role: string } }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const leaveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/team-members/leave", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.clear();
      toast({ title: "Left team", description: `You're no longer a member of ${membership.ownerName}'s team.` });
      navigate("/app/no-team");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to leave team. Please try again."), variant: "destructive" });
    },
  });

  return (
    <Card className="p-5 border-blue-200 bg-blue-50/60" data-testid="panel-membership">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm" data-testid="text-membership-owner">
              You're a member of <span className="text-blue-700">{membership.ownerName}</span>'s team
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Role: <Badge variant="secondary" className="ml-1 capitalize text-xs" data-testid="badge-membership-role">{membership.role}</Badge>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"
          onClick={() => leaveMutation.mutate()}
          disabled={leaveMutation.isPending}
          data-testid="button-leave-team"
        >
          {leaveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Leave team"}
        </Button>
      </div>
    </Card>
  );
}

export default function AppTeam() {
  usePageTitle("My Team");
  const [pathname, navigate] = useLocation();
  const urlSearch = useSearch();
  const params = new URLSearchParams(urlSearch);
  const initialPropertyId = params.get("propertyId");
  const propertyFilter = params.get("propertyId") || "all";
  const setPropertyFilter = (v: string) => {
    const next = new URLSearchParams(urlSearch);
    if (v === "all") next.delete("propertyId");
    else next.set("propertyId", v);
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = sortProperties(propertiesQuery.data ?? []);

  const authQuery = useQuery<AuthMeData>({ queryKey: ["/api/app/auth/me"] });
  const activeMembership = authQuery.data?.activeMembership ?? null;
  const { isOwner } = usePermissions();
  const showAuditLog = isOwner || activeMembership?.role === "admin";

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {initialPropertyId ? (
          <BackToPropertyLink propertyId={initialPropertyId} tab="administrators" />
        ) : null}
        <TeamPageBanner activeMembership={activeMembership} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-team-title" data-tour="team-heading">Team Members</h1>
            <p className="text-sm text-gray-500 mt-1">Invite team members and control their access to each area</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-[220px]">
              <SearchableSelect
                value={propertyFilter}
                onValueChange={setPropertyFilter}
                placeholder="All Properties"
                searchPlaceholder="Search properties..."
                emptyText="No properties match."
                testId="select-team-property-filter"
                options={[
                  { value: "all", label: "All Properties" },
                  ...properties.map((p) => ({
                    value: String(p.id),
                    label: p.name,
                    keywords: p.address ? `${p.name} ${p.address}` : undefined,
                  })),
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="team" />
              <GuideButton pageKey="team" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="team" />
        </div>

        {showAuditLog ? (
          <Tabs defaultValue="members">
            <TabsList>
              <TabsTrigger value="members" data-testid="tab-team-members">Members</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit-log">Audit Log</TabsTrigger>
            </TabsList>
            <TabsContent value="members" className="space-y-6 mt-6">
              {activeMembership && <MembershipPanel membership={activeMembership} />}
              <RoleProfilesGate />
              <TeamInviteSection activeMembership={activeMembership} />
              <PendingInvitations />
              <TeamMembersList propertyFilter={propertyFilter} />
            </TabsContent>
            <TabsContent value="audit" className="mt-6">
              <AuditLogTab properties={properties} />
            </TabsContent>
          </Tabs>
        ) : (
          <>
            {activeMembership && <MembershipPanel membership={activeMembership} />}
            <RoleProfilesGate />
            <TeamInviteSection activeMembership={activeMembership} />
            <PendingInvitations />
            <TeamMembersList propertyFilter={propertyFilter} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

function TeamPageBanner({ activeMembership }: { activeMembership: { ownerName: string } | null }) {
  const { canEdit } = usePermissions();
  if (!activeMembership) {
    return <ViewOnlyBanner area="team" />;
  }
  // The Team page exposes two independently-gated capabilities: member
  // management (the "team" area — editing a member's role/permissions and
  // removing members) and teammate management (the "invite" area — the invite
  // form and Role Profiles). Only show the page-level view-only banner when the
  // user can edit neither, otherwise an admin with invite:edit/team:view is
  // wrongly told "Editing is restricted" while they can fully use the invite
  // and Role Profiles sections.
  if (canEdit("team") || canEdit("invite")) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 mb-4" data-testid="banner-view-only">
      <Eye className="h-4 w-4 text-amber-600 shrink-0" />
      <span className="text-sm text-amber-700">You have view-only access to this section. Editing is restricted.</span>
    </div>
  );
}

function RoleProfilesGate() {
  const { canEdit: canEditArea } = usePermissions();
  if (!canEditArea("invite")) return null;
  return <RoleProfilesSection />;
}

function TeamInviteSection({ activeMembership }: { activeMembership: { ownerName: string } | null }) {
  const { canEdit: canEditArea } = usePermissions();
  const canInvite = canEditArea("invite");
  if (!canInvite) {
    if (activeMembership) {
      return (
        <Card className="p-6 border-gray-200 bg-gray-50" data-testid="card-member-invite-blocked">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-700">Invite a Team Member</h2>
          </div>
          <p className="text-sm text-gray-500">
            You don't have permission to invite team members. Contact <span className="font-medium text-gray-700">{activeMembership.ownerName}</span> to be granted invite access.
          </p>
        </Card>
      );
    }
    return null;
  }
  return <InviteForm />;
}

function RoleProfilesSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("invite");
  const [showDialog, setShowDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState<RoleProfile | null>(null);
  const [profileName, setProfileName] = useState("");
  const [profilePerms, setProfilePerms] = useState<AreaPermissions>({ ...DEFAULT_PERMISSIONS });
  const [profileGroups, setProfileGroups] = useState<string[] | null>(null);

  const profilesQuery = useQuery<RoleProfile[]>({
    queryKey: ["/api/app/role-profiles"],
    enabled: !readOnly,
  });

  const groupsQuery = usePropertyGroups();
  const availableGroups = (groupsQuery.data || []).map(g => g.name).sort();

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; permissions: AreaPermissions; propertyGroups: string[] | null }) => {
      await apiRequest("POST", "/api/app/role-profiles", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/role-profiles"] });
      toast({ title: "Profile created" });
      resetDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: parseErrorMessage(error, "Couldn't create this role profile. Please try again."), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; name: string; permissions: AreaPermissions; propertyGroups: string[] | null }) => {
      await apiRequest("PATCH", `/api/app/role-profiles/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/role-profiles"] });
      toast({ title: "Profile updated" });
      resetDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: parseErrorMessage(error, "Couldn't update this role profile. Please try again."), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/role-profiles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/role-profiles"] });
      toast({ title: "Profile deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: parseErrorMessage(error, "Couldn't delete this role profile. Please try again."), variant: "destructive" });
    },
  });

  const profiles = profilesQuery.data || [];

  function resetDialog() {
    setShowDialog(false);
    setEditingProfile(null);
    setProfileName("");
    setProfilePerms({ ...DEFAULT_PERMISSIONS });
    setProfileGroups(null);
  }

  function openCreate() {
    setEditingProfile(null);
    setProfileName("");
    setProfilePerms({ ...DEFAULT_PERMISSIONS });
    setProfileGroups(null);
    setShowDialog(true);
  }

  function openEdit(profile: RoleProfile) {
    setEditingProfile(profile);
    setProfileName(profile.name);
    setProfilePerms(profile.permissions as AreaPermissions);
    setProfileGroups((profile.propertyGroups as string[] | null) ?? null);
    setShowDialog(true);
  }

  function handleSave() {
    if (!profileName.trim()) return;
    const data = { name: profileName.trim(), permissions: profilePerms, propertyGroups: profileGroups };
    if (editingProfile) {
      updateMutation.mutate({ id: editingProfile.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookUser className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900" data-testid="text-profiles-title" data-tour="team-permissions">Role Profiles</h2>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="button-create-profile" disabled={readOnly}>
          <Plus className="h-4 w-4 mr-1" />
          New Profile
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Create reusable permission templates. When inviting a new team member, select a profile to auto-fill their permissions.
      </p>

      {readOnly ? (
        <div className="text-center py-6 border border-dashed rounded-lg" data-testid="state-profiles-locked">
          <Lock className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Only the team owner can manage role profiles.</p>
        </div>
      ) : profilesQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <BookUser className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-400">No profiles yet. Create one to speed up invitations.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {profiles.map((profile) => {
            const perms = profile.permissions as AreaPermissions;
            const editCount = APP_AREAS.filter((a) => perms[a] === "edit").length;
            const viewCount = APP_AREAS.filter((a) => perms[a] === "view").length;
            const disabledCount = APP_AREAS.filter((a) => perms[a] === "disabled").length;
            return (
              <div
                key={profile.id}
                className="border rounded-lg p-4 bg-white hover:border-blue-300 transition-colors"
                data-testid={`card-profile-${profile.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{profile.name}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => openEdit(profile)}
                      data-testid={`button-edit-profile-${profile.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteMutation.mutate(profile.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`button-delete-profile-${profile.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {editCount > 0 && (
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">{editCount} Edit</Badge>
                  )}
                  {viewCount > 0 && (
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">{viewCount} View</Badge>
                  )}
                  {disabledCount > 0 && (
                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">{disabledCount} Disabled</Badge>
                  )}
                  {(() => {
                    const groups = (profile.propertyGroups as string[] | null) ?? null;
                    if (groups === null) return null;
                    const stale = groups.filter((g) => !availableGroups.includes(g));
                    return (
                      <>
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          <FolderOpen className="h-3 w-3 mr-1" />
                          {groups.length === 0 ? "No groups" : groups.join(", ")}
                        </Badge>
                        {stale.length > 0 && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-amber-50 text-amber-800 border-amber-300"
                            title={`No longer exists: ${stale.join(", ")}. Edit this profile to remove ${stale.length === 1 ? "it" : "them"}.`}
                            data-testid={`badge-profile-stale-${profile.id}`}
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {stale.length} missing
                          </Badge>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetDialog(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? "Edit Role Profile" : "Create Role Profile"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Profile Name</label>
              <Input
                placeholder="e.g. Accounting, Maintenance Lead"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                data-testid="input-profile-name"
              />
            </div>
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-700">Permissions</span>
              </div>
              <PermissionGrid
                permissions={profilePerms}
                onChange={(area, level) => {
                  if (area === "team" && level === "edit") return;
                  if (area === "invite" && level === "view") return;
                  setProfilePerms((prev) => ({ ...prev, [area]: level }));
                }}
                onBulkChange={(level) => {
                  const bulk = {} as AreaPermissions;
                  APP_AREAS.forEach((area) => {
                    if (area === "team" && level === "edit") bulk[area] = "view";
                    else if (area === "invite" && level === "view") bulk[area] = "disabled";
                    else bulk[area] = level;
                  });
                  setProfilePerms(bulk);
                }}
              />
            </div>
            <PropertyGroupPicker
              availableGroups={availableGroups}
              selectedGroups={profileGroups}
              onChange={setProfileGroups}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving || !profileName.trim()} data-testid="button-save-profile">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              {editingProfile ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const ADMIN_PERMISSIONS: AreaPermissions = {
  properties: "edit", underwriting: "edit", image_generator: "edit",
  messages: "edit", pricing: "edit", maintenance: "edit", devices: "edit",
  financials: "edit", members: "edit", furnishing: "edit", padsplit_sync: "edit", team: "view",
  invite: "edit", owners: "edit",
};


function InviteForm() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("invite");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [permissions, setPermissions] = useState<AreaPermissions>({ ...DEFAULT_PERMISSIONS });
  const [propertyGroups, setPropertyGroups] = useState<string[] | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("custom");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const teamLimitQuery = useQuery<{ plan: string; current: number; limit: number | null }>({
    queryKey: ["/api/app/team-limit"],
  });
  const teamLimit = teamLimitQuery.data;
  const atLimit = teamLimit && teamLimit.limit !== null && teamLimit.current >= teamLimit.limit;

  const profilesQuery = useQuery<RoleProfile[]>({
    queryKey: ["/api/app/role-profiles"],
    enabled: !readOnly,
  });
  const profiles = profilesQuery.data || [];
  const groupsQuery = usePropertyGroups();
  const availableGroups = (groupsQuery.data || []).map(g => g.name).sort();

  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; name?: string; role: string; permissions: AreaPermissions; propertyGroups: string[] | null }) => {
      await apiRequest("POST", "/api/app/team-members", data);
    },
    onSuccess: () => {
      setFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-limit"] });
      toast({ title: "Invitation sent", description: `Invited ${email}` });
      setEmail("");
      setName("");
      setRole("member");
      setPermissions({ ...DEFAULT_PERMISSIONS });
      setPropertyGroups(null);
      setSelectedProfileId("custom");
    },
    onError: (error: Error) => {
      const next = parseFieldErrors(error);
      if (Object.keys(next).length > 0) {
        setFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      if (parseErrorCode(error) === "INVITEE_ALREADY_AFFILIATED") {
        toast({
          title: "Could not send invitation",
          description: "This person is already a member of another team and cannot be added.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Could not send invitation",
        description: parseErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const handlePermChange = (area: AppArea, level: PermissionLevel) => {
    if (area === "team" && level === "edit") return;
    if (area === "invite" && level === "view") return;
    setPermissions((prev) => ({ ...prev, [area]: level }));
    setSelectedProfileId("custom");
  };

  const handleProfileSelect = (val: string) => {
    setSelectedProfileId(val);
    if (val === "custom") return;
    const profile = profiles.find((p) => String(p.id) === val);
    if (profile) {
      setPermissions(profile.permissions as AreaPermissions);
    }
  };

  const handleRoleChange = (val: string) => {
    setRole(val);
    if (val === "admin") {
      setPermissions({ ...ADMIN_PERMISSIONS });
      setPropertyGroups(null);
    } else if (val === "member") {
      setPermissions({ ...DEFAULT_PERMISSIONS });
      setPropertyGroups(null);
    } else {
      const profile = profiles.find((p) => p.name === val);
      if (profile) {
        const perms = profile.permissions as AreaPermissions;
        setPermissions({ ...perms, team: perms.team === "edit" ? "view" : perms.team });
        setPropertyGroups((profile.propertyGroups as string[] | null) ?? null);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setFieldErrors({});
    inviteMutation.mutate({ email: email.trim(), name: name.trim() || undefined, role, permissions, propertyGroups });
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="h-5 w-5 text-gray-600" />
        <h2 className="text-lg font-semibold text-gray-900" data-testid="text-invite-title" data-tour="team-invite">Invite a Team Member</h2>
        {teamLimit && teamLimit.limit !== null && (
          <span className="ml-auto text-xs text-gray-500" data-testid="text-team-limit">
            {teamLimit.current} / {teamLimit.limit} users
          </span>
        )}
      </div>
      {atLimit && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="banner-team-limit">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            You've reached the team member limit for your <strong className="capitalize">{teamLimit?.plan}</strong> plan.{" "}
            <Link href="/app/account/billing" className="underline font-medium hover:text-amber-900" data-testid="link-upgrade-plan">Upgrade your plan</Link> to invite more.
          </span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
              required
              className={fieldErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="input-invite-email"
            />
            {fieldErrors.email && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-invite-email">{fieldErrors.email}</p>
            )}
          </div>
          <div className="w-full sm:w-[160px]">
            <Input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => { setName(e.target.value); clearFieldError("name"); }}
              className={fieldErrors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="input-invite-name"
            />
            {fieldErrors.name && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-invite-name">{fieldErrors.name}</p>
            )}
          </div>
          <div className="w-full sm:w-[180px]">
            <Select value={role} onValueChange={handleRoleChange}>
              <SelectTrigger data-testid="select-invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                {profiles.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 border-t mt-1 pt-1.5">Custom Roles</div>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">Area Permissions</span>
            </div>
            {profiles.length > 0 && (
              <div className="w-[180px]">
                <Select value={selectedProfileId} onValueChange={handleProfileSelect}>
                  <SelectTrigger className="h-8 text-xs" data-testid="select-invite-profile">
                    <SelectValue placeholder="Select profile..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom</SelectItem>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <PermissionGrid
            permissions={permissions}
            onChange={handlePermChange}
            onBulkChange={(level) => {
              const bulk = {} as AreaPermissions;
              APP_AREAS.forEach((area) => {
                if (area === "team" && level === "edit") bulk[area] = "view";
                else if (area === "invite" && level === "view") bulk[area] = "disabled";
                else bulk[area] = level;
              });
              setPermissions(bulk);
              setSelectedProfileId("custom");
            }}
          />
        </div>

        <PropertyGroupPicker
          availableGroups={availableGroups}
          selectedGroups={propertyGroups}
          onChange={setPropertyGroups}
        />

        <Button type="submit" disabled={inviteMutation.isPending || readOnly || !!atLimit} data-testid="button-send-invite">
          {inviteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
          Send Invitation
        </Button>
      </form>
    </Card>
  );
}

function PendingInvitations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invitationsQuery = useQuery<TeamInvitation[]>({
    queryKey: ["/api/app/team-invitations"],
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: "accept" | "decline" }) => {
      await apiRequest("PATCH", `/api/app/team-invitations/${id}`, { action });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/my-permissions"] });
      toast({
        title: variables.action === "accept" ? "Invitation accepted" : "Invitation declined",
        description: variables.action === "accept"
          ? "You now have access to their properties"
          : "The invitation has been declined",
      });
    },
    onError: (error: Error) => {
      if (parseErrorCode(error) === "ALREADY_AFFILIATED") {
        toast({
          title: "Cannot accept invitation",
          description: "You are already a member of another team and cannot join a second team.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Error",
        description: parseErrorMessage(error, "Failed to respond to invitation"),
        variant: "destructive",
      });
    },
  });

  const invitations = invitationsQuery.data || [];

  if (invitationsQuery.isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-12 w-full" />
      </Card>
    );
  }

  if (invitations.length === 0) return null;

  return (
    <Card className="p-6 border-blue-200 bg-blue-50/50">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900" data-testid="text-invitations-title">
          Pending Invitations ({invitations.length})
        </h2>
      </div>
      <div className="space-y-3">
        {invitations.map((inv) => {
          const perms = (inv.permissions || DEFAULT_PERMISSIONS) as AreaPermissions;
          const enabledAreas = APP_AREAS.filter((a) => perms[a] !== "disabled");
          return (
            <div key={inv.id} className="bg-white rounded-lg border p-4" data-testid={`card-invitation-${inv.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900">{inv.ownerName}</p>
                  <p className="text-sm text-gray-500">invited you as <Badge variant="secondary">{inv.role}</Badge></p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => respondMutation.mutate({ id: inv.id, action: "accept" })}
                    disabled={respondMutation.isPending}
                    data-testid={`button-accept-${inv.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => respondMutation.mutate({ id: inv.id, action: "decline" })}
                    disabled={respondMutation.isPending}
                    data-testid={`button-decline-${inv.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>
              {enabledAreas.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {enabledAreas.map((area) => (
                    <Badge key={area} variant="outline" className="text-xs">
                      {AREA_LABELS[area]}: {perms[area]}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BookingAlertPrefs({
  member,
  hasMembersAccess,
  onChange,
  disabled,
}: {
  member: EnrichedTeamMember;
  hasMembersAccess: boolean;
  onChange: (patch: { bookingRequestBellAlerts?: boolean; bookingRequestEmailAlerts?: boolean }) => void;
  disabled?: boolean;
}) {
  const bellOn = member.bookingRequestBellAlerts !== false;
  const emailOn = member.bookingRequestEmailAlerts !== false;
  return (
    <div className="border rounded-lg p-4 bg-gray-50" data-testid={`booking-alerts-${member.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <Bell className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-semibold text-gray-700">Booking-request alerts</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Choose whether this teammate is notified when a new booking request is submitted.
        {!hasMembersAccess && (
          <span className="block mt-1 text-amber-700">
            Give them Members access for these alerts to take effect.
          </span>
        )}
      </p>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer" data-testid={`checkbox-booking-bell-${member.id}`}>
          <Checkbox
            checked={bellOn}
            disabled={disabled}
            onCheckedChange={(val) => onChange({ bookingRequestBellAlerts: val === true })}
          />
          <span className="text-sm text-gray-700">In-app (bell) notification</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer" data-testid={`checkbox-booking-email-${member.id}`}>
          <Checkbox
            checked={emailOn}
            disabled={disabled}
            onCheckedChange={(val) => onChange({ bookingRequestEmailAlerts: val === true })}
          />
          <span className="text-sm text-gray-700">Email notification</span>
        </label>
      </div>
    </div>
  );
}

function TeamMembersList({ propertyFilter }: { propertyFilter: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("team");
  const canResendInvite = canEditArea("invite");

  const membersQuery = useQuery<EnrichedTeamMember[]>({
    queryKey: ["/api/app/team-members"],
  });

  const profilesQuery = useQuery<RoleProfile[]>({
    queryKey: ["/api/app/role-profiles"],
    enabled: !readOnly,
  });
  const profiles = profilesQuery.data || [];

  const groupsQuery = usePropertyGroups();
  const availableGroups = (groupsQuery.data || []).map(g => g.name).sort();
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; role?: string; permissions?: Partial<AreaPermissions>; propertyGroups?: string[] | null }) => {
      await apiRequest("PATCH", `/api/app/team-members/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-members"] });
      toast({ title: "Team member updated" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update team member", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/team-members/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-limit"] });
      toast({ title: "Team member removed" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove member", variant: "destructive" });
    },
  });

  const [resendingIds, setResendingIds] = useState<Set<number>>(new Set());
  const resendMutation = useMutation({
    mutationFn: async (id: number) => {
      setResendingIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      try {
        await apiRequest("POST", `/api/app/team-members/${id}/resend-invite`);
      } finally {
        setResendingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-members"] });
      toast({ title: "Invite resent", description: "The invitation email has been sent again." });
    },
    onError: (err: any) => {
      const msg = parseErrorMessage(err, "Failed to resend invite");
      toast({ title: "Could not resend", description: msg, variant: "destructive" });
    },
  });

  const allMembers = membersQuery.data || [];
  const members = allMembers.filter((m) => {
    if (readOnly) return true;
    if (propertyFilter === "all") return true;
    const perms = (m.permissions || DEFAULT_PERMISSIONS) as AreaPermissions;
    return perms.properties !== "disabled";
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  function applyProfileToMember(memberId: number, profileId: string) {
    const profile = profiles.find((p) => String(p.id) === profileId);
    if (profile) {
      const perms = profile.permissions as AreaPermissions;
      updateMutation.mutate({
        id: memberId,
        role: profile.name,
        permissions: { ...perms, team: perms.team === "edit" ? "view" : perms.team },
        propertyGroups: (profile.propertyGroups as string[] | null) ?? null,
      });
    }
  }

  function handleMemberRoleChange(memberId: number, val: string) {
    if (val === "admin") {
      updateMutation.mutate({ id: memberId, role: "admin", permissions: { ...ADMIN_PERMISSIONS } });
    } else if (val === "member") {
      updateMutation.mutate({ id: memberId, role: "member", permissions: { ...DEFAULT_PERMISSIONS } });
    } else {
      const profile = profiles.find((p) => p.name === val);
      if (profile) {
        const perms = profile.permissions as AreaPermissions;
        updateMutation.mutate({
          id: memberId,
          role: val,
          permissions: { ...perms, team: perms.team === "edit" ? "view" : perms.team },
          propertyGroups: (profile.propertyGroups as string[] | null) ?? null,
        });
      } else {
        updateMutation.mutate({ id: memberId, role: val });
      }
    }
  }
  return (
    <Card>
      <div className="p-6 pb-0 flex items-center gap-2">
        <Users className="h-5 w-5 text-gray-600" />
        <h2 className="text-lg font-semibold text-gray-900" data-testid="text-members-title" data-tour="team-list">
          Your Team ({members.length})
        </h2>
      </div>

      {membersQuery.isLoading ? (
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="p-12 text-center">
          <Users className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-500" data-testid="text-no-members">No team members yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Use the invite form above to add team members
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited</TableHead>
                <TableHead className="w-[130px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const memberPerms = (member.permissions || DEFAULT_PERMISSIONS) as AreaPermissions;
                const isExpanded = expandedId === member.id;
                return (
                  <Fragment key={member.id}>
                    <TableRow data-testid={`row-member-${member.id}`}>
                      <TableCell className="font-medium">{member.email}</TableCell>
                      <TableCell className="text-gray-600">{member.name || "—"}</TableCell>
                      <TableCell>
                        {member.role === "owner" ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200">Owner</Badge>
                        ) : readOnly ? (
                          <Badge variant="outline" className="text-gray-700" data-testid={`badge-role-${member.id}`}>{member.role}</Badge>
                        ) : (
                          <Select
                            value={member.role}
                            onValueChange={(val) => handleMemberRoleChange(member.id, val)}
                          >
                            <SelectTrigger className="w-[140px] h-8" data-testid={`select-role-${member.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              {profiles.length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 border-t mt-1 pt-1.5">Custom Roles</div>
                                  {profiles.map((p) => (
                                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                                  ))}
                                </>
                              )}
                              {member.role !== "admin" && member.role !== "member" && !profiles.some(p => p.name === member.role) && (
                                <SelectItem value={member.role}>{member.role}</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          let label = member.status;
                          let tooltip = "";
                          if (member.status === "pending") {
                            if (member.inviteeAccountExists === false) {
                              label = "Invited";
                              tooltip = "Ask them to check their email and sign up";
                            } else {
                              label = "Awaiting response";
                              tooltip = "Ask them to log in and respond to the invite";
                            }
                          } else if (member.status === "accepted") {
                            tooltip = "Active team member";
                          } else if (member.status === "declined") {
                            tooltip = "They declined the invitation";
                          }
                          return (
                            <div className="flex items-center gap-1.5" title={tooltip} data-testid={`status-${member.id}`}>
                              {statusIcon(member.status)}
                              <span className="text-sm capitalize">{label}</span>
                            </div>
                          );
                        })()}
                        {(() => {
                          const groups = (member.propertyGroups as string[] | null) ?? null;
                          if (groups === null) return null;
                          const stale = groups.filter((g) => !availableGroups.includes(g));
                          return (
                            <div className="flex flex-wrap gap-1 mt-1">
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200" data-testid={`badge-groups-${member.id}`}>
                                <FolderOpen className="h-3 w-3 mr-1" />
                                {groups.length === 0 ? "No groups" : groups.join(", ")}
                              </Badge>
                              {stale.length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-amber-50 text-amber-800 border-amber-300"
                                  title={`No longer exists: ${stale.join(", ")}. Open permissions to remove ${stale.length === 1 ? "it" : "them"}.`}
                                  data-testid={`badge-member-stale-${member.id}`}
                                >
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  {stale.length} missing
                                </Badge>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        <div data-testid={`text-invited-at-${member.id}`}>{formatDate(member.invitedAt)}</div>
                        {member.status === "pending" && (() => {
                          const lastSent = formatLastSent(member.lastInviteSentAt ?? member.invitedAt);
                          if (!lastSent) return null;
                          return (
                            <div
                              className="text-xs text-gray-400 mt-0.5"
                              title={member.lastInviteSentAt ? "Time since the invite was last (re-)sent" : "Time since the original invite was sent"}
                              data-testid={`text-last-sent-${member.id}`}
                            >
                              Last sent {lastSent}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {member.status === "pending" && canResendInvite && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 text-xs gap-1"
                              onClick={() => resendMutation.mutate(member.id)}
                              disabled={resendingIds.has(member.id)}
                              data-testid={`button-resend-${member.id}`}
                              title="Resend invite email"
                            >
                              {resendingIds.has(member.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                              Resend
                            </Button>
                          )}
                          {member.role !== "owner" && !readOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setExpandedId(isExpanded ? null : member.id)}
                              data-testid={`button-perms-${member.id}`}
                              title="Edit permissions"
                            >
                              <Shield className="h-4 w-4" />
                            </Button>
                          )}
                          {member.role !== "owner" && !readOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                if (confirm(`Remove ${member.name || member.email} from the team? They will be signed out and notified by email.`)) {
                                  removeMutation.mutate(member.id);
                                }
                              }}
                              disabled={removeMutation.isPending}
                              data-testid={`button-remove-${member.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`perms-${member.id}`}>
                        <TableCell colSpan={6}>
                          <div className="py-2 px-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 text-gray-600" />
                                <span className="text-sm font-semibold text-gray-700">
                                  Permissions for {member.name || member.email}
                                </span>
                              </div>
                              {profiles.length > 0 && (
                                <Select onValueChange={(val) => applyProfileToMember(member.id, val)}>
                                  <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`select-apply-profile-${member.id}`}>
                                    <SelectValue placeholder="Apply Profile..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {profiles.map((p) => (
                                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                            <PermissionGrid
                              permissions={memberPerms}
                              onChange={(area, level) => {
                                if (area === "team" && level === "edit") return;
                                if (area === "invite" && level === "view") return;
                                updateMutation.mutate({
                                  id: member.id,
                                  permissions: { [area]: level },
                                });
                              }}
                              onBulkChange={(level) => {
                                const bulk = {} as AreaPermissions;
                                APP_AREAS.forEach((area) => {
                                  if (area === "team" && level === "edit") bulk[area] = "view";
                                  else if (area === "invite" && level === "view") bulk[area] = "disabled";
                                  else bulk[area] = level;
                                });
                                updateMutation.mutate({
                                  id: member.id,
                                  permissions: bulk,
                                });
                              }}
                              disabled={updateMutation.isPending}
                            />
                            <div className="mt-4">
                              <PropertyGroupPicker
                                availableGroups={availableGroups}
                                selectedGroups={(member.propertyGroups as string[] | null) ?? null}
                                onChange={(groups) => {
                                  updateMutation.mutate({
                                    id: member.id,
                                    propertyGroups: groups,
                                  });
                                }}
                                disabled={updateMutation.isPending}
                              />
                            </div>
                            <div className="mt-4">
                              <BookingAlertPrefs
                                member={member}
                                hasMembersAccess={memberPerms.members !== "disabled"}
                                onChange={(patch) => updateMutation.mutate({ id: member.id, ...patch })}
                                disabled={updateMutation.isPending}
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
